// humanize — turn a proposal's machine-facing details into typed descriptors
// a localiser renders as plain language. PURE: no i18n, no I/O. The review
// surface (ProposalReview.tsx) maps each descriptor to a Lingui string, so
// the parsing stays fully unit-testable without a message catalogue.
//
// Why descriptors and not strings: the author must never see a raw element
// id-path ("q1 › q1c1") or a literal attrs JSON ("{\"correct\":true}"). A
// subject-matter expert has no idea what `q1c1` means. These parsers recover
// the *human* shape (which question, which option, what the change does) that
// the surface then phrases in the author's language.

import { exhaustSwitchCase } from "@bilda/utils";

// === Element reference ===
//
// Where a change lands, described structurally rather than as a raw id-path.
export type ElementRef =
    | { readonly type: "question"; readonly n: number }
    | { readonly type: "option"; readonly q: number; readonly label: string }
    | { readonly type: "stem"; readonly q: number }
    | { readonly type: "unknown" };

// Agent-assigned ids follow short, stable conventions (the server tool
// contract standardises on these — see `create_material` / `propose_edit`):
//   q<n>          → question n
//   q<n>-stem     → the stem of question n
//   q<n>-opt-<x>  → option <x> of question n   (letter form)
//   q<n>c<m>      → choice m of question n      (numbered form)
// The LAST path segment names the target and embeds its question number, so
// we parse that. Anything off-convention yields `unknown` — the surface then
// phrases it generically ("this part of the material"), never the raw id.
const QUESTION = /^q(\d+)$/;
const STEM = /^q(\d+)-stem$/;
const OPTION_LETTER = /^q(\d+)-opt-([a-z])$/;
const OPTION_NUMBER = /^q(\d+)c(\d+)$/;

export const describeElementPath = (path: readonly string[]): ElementRef => {
    const seg = path.at(-1);
    if (seg === undefined) return { type: "unknown" };

    const stem = STEM.exec(seg);
    if (stem !== null) return { type: "stem", q: Number(stem[1]) };

    const letter = OPTION_LETTER.exec(seg);
    if (letter !== null)
        return {
            type: "option",
            q: Number(letter[1]),
            label: letter[2].toUpperCase(),
        };

    const numbered = OPTION_NUMBER.exec(seg);
    if (numbered !== null)
        return { type: "option", q: Number(numbered[1]), label: numbered[2] };

    const question = QUESTION.exec(seg);
    if (question !== null) return { type: "question", n: Number(question[1]) };

    return { type: "unknown" };
};

// === Attribute change ===
//
// What a `set_element_attrs` patch does, described as an intent rather than a
// literal JSON blob. Known keys map to a concrete phrasing; anything else
// degrades to a generic, honest "adjusts this" — never a dumped `{...}`.
export type AttrChange =
    | { readonly type: "mark_correct" }
    | { readonly type: "unmark_correct" }
    | { readonly type: "generic" };

// The attr keys we can phrase precisely. New known keys join this tuple and
// the `describeKnownKey` switch below; `exhaustSwitchCase` forces the pairing.
const KNOWN_KEYS = ["correct"] as const;
type KnownKey = (typeof KNOWN_KEYS)[number];

// `some` compares string-to-string (no cast) while narrowing to KnownKey.
const isKnownKey = (key: string): key is KnownKey =>
    KNOWN_KEYS.some((known) => known === key);

const describeKnownKey = (key: KnownKey, value: unknown): AttrChange => {
    switch (key) {
        case "correct":
            if (value === true) return { type: "mark_correct" };
            if (value === false) return { type: "unmark_correct" };
            return { type: "generic" };
        default:
            return exhaustSwitchCase(key);
    }
};

export const describeAttrChange = (
    attrs: Readonly<Record<string, unknown>>,
): AttrChange => {
    const keys = Object.keys(attrs);
    // Only the clean single-known-key shape gets a precise phrasing; a mixed
    // or unrecognised bag stays generic rather than risk a misleading claim.
    const only = keys.length === 1 ? keys[0] : undefined;
    if (only !== undefined && isKnownKey(only))
        return describeKnownKey(only, attrs[only]);
    return { type: "generic" };
};
